// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, CircularProgress, IconButton, ToggleButton, ToggleButtonGroup, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Link,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';

// The reading half of Feedback.js. Without it the widget is a write-only hole,
// which is worse than no widget at all — people file reports, hear nothing, and
// stop filing them.

const STATUSES = ['new', 'triaged', 'done', 'declined'];
const STATUS_COLOR = { new: 'warning', triaged: 'info', done: 'success', declined: 'default' };
const SEVERITY_COLOR = { blocking: 'error', annoying: 'warning', minor: 'default' };

export default function FeedbackAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('new');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // { row, status, note }

  const me = getSession();
  const iAmAdmin = !!me && ((me.groups || []).includes('OptimumAdmins') || me.roleId === 'admin');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const list = await api('/feedback');
      if (!Array.isArray(list)) throw new Error('The feedback service returned an unexpected response.');
      setRows(list);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );
  const newCount = useMemo(() => rows.filter((r) => r.status === 'new').length, [rows]);

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api('/feedback', {
        method: 'PUT',
        body: JSON.stringify({ id: edit.row.id, status: edit.status, note: edit.note }),
      });
      setEdit(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Attachments are S3 keys under the reporter's own tenant prefix, so the
  // existing document rules decide who may fetch one. The URL is minted on
  // demand and short-lived rather than stored on the row.
  const openAttachment = async (key) => {
    setErr('');
    try {
      const { url } = await api(`/documents/url?key=${encodeURIComponent(key)}`);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { setErr(e.message); }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#021F4B' }}>Feedback</Typography>
        {!!newCount && <Chip size="small" color="warning" label={`${newCount} new`} />}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)}>
          {['new', 'triaged', 'done', 'all'].map((s) => (
            <ToggleButton key={s} value={s} sx={{ textTransform: 'none' }}>{s}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
      </Stack>

      <Typography variant="body2" sx={{ color: '#5C6B7A', mb: 2 }}>
        Sent from the feedback control in the top bar, which everyone can reach. Each report carries
        the screen, workspace and browser it came from, so it can be reproduced without going back
        to the person who filed it.
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Report</TableCell>
                <TableCell>Where</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const files = Array.isArray(r.attachments) ? r.attachments : [];
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                        <Chip size="small" label={r.kind} variant="outlined" sx={{ height: 18, fontSize: 10.5 }} />
                        <Chip size="small" label={r.severity} color={SEVERITY_COLOR[r.severity] || 'default'}
                          sx={{ height: 18, fontSize: 10.5 }} />
                      </Stack>
                      <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{r.title}</Typography>
                      {r.body && (
                        <Typography sx={{ fontSize: 12.5, color: '#5C6B7A', whiteSpace: 'pre-wrap' }}>{r.body}</Typography>
                      )}
                      {!!files.length && (
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          {files.map((f) => (
                            <Link key={f.key} component="button" type="button" underline="hover"
                              onClick={() => openAttachment(f.key)}
                              sx={{ fontSize: 12, color: '#0D5B9A', fontWeight: 600 }}>
                              {f.name}
                            </Link>
                          ))}
                        </Stack>
                      )}
                      {r.note && (
                        <Typography sx={{ fontSize: 12, color: '#2E7D32', mt: 0.5 }}>
                          {r.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <div>{r.screen || '—'}</div>
                      <Typography sx={{ fontSize: 11, color: '#9AA6B2' }}>
                        {r.workspace ? `workspace ${r.workspace}` : ''}
                        {r.viewport ? ` · ${r.viewport}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {r.created_by}
                      <Typography sx={{ fontSize: 11, color: '#9AA6B2' }}>
                        {r.tenant_id}
                        {r.created_at ? ` · ${String(r.created_at).slice(0, 10)}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} sx={{ height: 20 }} />
                    </TableCell>
                    <TableCell align="right">
                      {iAmAdmin && (
                        <Tooltip title="Triage">
                          <IconButton size="small" onClick={() => setEdit({ row: r, status: r.status, note: r.note || '' })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                    {filter === 'new' ? 'Nothing new.' : 'No reports here.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!edit} onClose={() => !busy && setEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: '#021F4B' }}>Triage</DialogTitle>
        {edit && (
          <DialogContent dividers>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 2 }}>{edit.row.title}</Typography>
            <TextField select fullWidth size="small" label="Status" value={edit.status} sx={{ mb: 2 }}
              onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField fullWidth size="small" multiline minRows={2} label="Note" value={edit.note}
              onChange={(e) => setEdit({ ...edit, note: e.target.value })}
              helperText="Kept on the report as the record of what was decided." />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
